import { useSuspenseQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { get, PaginatedResponse } from "@/api/api";
import { SublocationData } from "@/sublocation/sublocation";
import { apiClient } from "@/api/client";
import { useSelectedOrganizationId } from "@/organization/api";

export const sublocations = createQueryKeys('sublocations', {
  detail: (
    sublocationId: number,
    organizationId: number,
    locationId: number,
  ) => ({
    queryKey: [sublocationId, organizationId, locationId],
    queryFn: async () => {
      const url = `/organizations/${organizationId}/locations/${locationId}/sublocations/${sublocationId}/`
      return await get<SublocationData>(url);
    },
  }),
  list: (
    locationId: number,
    organizationId: number,
  ) => ({
    queryKey: [locationId, organizationId],
    queryFn: async () => {
      const url = `/organizations/${organizationId}/locations/${locationId}/sublocations/`
      return await get<PaginatedResponse<SublocationData>>(url);
    },
  }),
});

export const useSublocations = (locationId: number) => {
  const selectedOrgId = useSelectedOrganizationId();
  return useSuspenseQuery(sublocations.list(locationId, selectedOrgId));
};

export type CreateSublocationInput = {
  name: string;
  location: number;
};

export const useCreateSublocation = () => {
  const queryClient = useQueryClient();
  const selectedOrgId = useSelectedOrganizationId();

  return useMutation({
    mutationFn: async (data: CreateSublocationInput) => {
      const res = await apiClient.post(`/organizations/${selectedOrgId}/locations/${data.location}/sublocations/`, data);
      return res.data as SublocationData;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sublocations._def });
    },
  });
};

export type BulkUpdatePositionsInput = {
  sublocationId: number;
  locationId: number;
  positions: Array<{
    installationId: number;
    grid_x: number;
    grid_y: number;
  }>;
};

export const useBulkUpdatePositions = () => {
  const queryClient = useQueryClient();
  const selectedOrgId = useSelectedOrganizationId();

  return useMutation({
    mutationFn: async (data: BulkUpdatePositionsInput) => {
      const results = [];
      for (const pos of data.positions) {
        const url = `/organizations/${selectedOrgId}/locations/${data.locationId}/sublocations/${data.sublocationId}/installations/${pos.installationId}/`;
        const res = await apiClient.patch(url, {
          position: { grid_x: pos.grid_x, grid_y: pos.grid_y }
        });
        results.push(res.data);
      }
      return results;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sublocations._def });
    },
  });
};
